import { Controller } from "@hotwired/stimulus"

import { getConsumer } from "@shared/lib/action-cable/consumer"

// CONEK-4570 (P5 voice-memo) — Subscribes to VoiceMemoInsightChannel and
// reloads the voice-memo draft Turbo Frame when AI::AnalyzeVoiceMemoJob
// finishes async analysis. Clone of telephony/call_insight_controller.js
// (CONEK-4035), swapping the channel name and matching on entity_id instead
// of call_session_id (a voice memo is anchored to a ContactCompany, not a
// CallSession).
export default class extends Controller {
  static values = {
    entityId: String,
    frameUrl: String,
    spaceGroupId: String,
  }

  connect() {
    this.#subscribe()
  }

  disconnect() {
    this.subscription?.unsubscribe()
  }

  #subscribe() {
    const consumer = getConsumer(this.spaceGroupIdValue || "default")
    this.subscription = consumer.subscriptions.create(
      { channel: "VoiceMemoInsightChannel" },
      {
        received: (data) => this.#handleMessage(data),
      },
    )
  }

  #handleMessage(data) {
    // "ready" → analysis produced a draft; "failed" → analysis produced NO
    // draft (err Result). BOTH are terminal and must clear the spinner —
    // without handling "failed" the "Drafting your suggestion…" state (no
    // client-side timeout) would spin forever on a provider failure.
    if (data.action !== "ready" && data.action !== "failed") return
    if (data.entity_id !== this.entityIdValue) return
    // Defensive cross-tenant guard: the user's stream can carry events for
    // entities in another tenant. Drop any payload not scoped to this card's
    // space group when both sides know it.
    if (
      this.spaceGroupIdValue &&
      data.space_group_id &&
      data.space_group_id !== this.spaceGroupIdValue
    )
      return

    const frame = document.getElementById(
      `voice_memo_draft_${this.entityIdValue}`,
    )
    if (!frame) return

    // Set src to trigger the Turbo Frame to fetch the updated partial. On
    // failure, flag the reload so the server renders the error/retry state
    // instead of the (now-stale) drafting spinner — the frame's base URL
    // carries no `analyzing` param, so a bare reload would render an empty
    // card and silently swallow the failure.
    if (data.action === "failed") {
      const url = new URL(this.frameUrlValue, window.location.origin)
      url.searchParams.set("voice_memo_failed", "1")
      frame.src = url.pathname + url.search
    } else {
      frame.src = this.frameUrlValue
    }
  }
}
